import React, { useState, useEffect } from 'react';
import { GOLD } from '../constants/theme';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Menu', href: '#menu', sub: ['Starters', 'Mains', 'Desserts', 'Beverages'] },
  { label: 'Cuisine', href: '#cuisine', sub: ['Indian', 'Continental', 'Asian', 'Mediterranean'] },
  { label: 'Live Station', href: '#live', sub: ['BBQ Station', 'Chaat Corner', 'Pasta Live', 'Dessert Bar'] },
  { label: 'About Us', href: '#about' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Contact', href: '#contact' },
];

const BREAKPOINT = 992;

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [drop, setDrop] = useState<string | null>(null);
  const [mob, setMob] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < BREAKPOINT);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < BREAKPOINT);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (mob) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [mob]);

  return (
    <>
      <nav style={{
        position:'fixed', top:0, left:0, right:0, zIndex:1000,
        background: scrolled ? 'rgba(8,8,8,0.97)' : 'transparent',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        borderBottom: scrolled ? `1px solid rgba(196,152,50,0.2)` : 'none',
        transition:'all 0.4s', padding: isMobile ? '0 4vw' : '0 5vw',
        display:'flex', alignItems:'center', justifyContent:'space-between', height: isMobile ? 64 : 76,
      }}>
        {/* Logo */}
        <a href="#home" style={{ display:'flex', alignItems:'center', gap: isMobile ? 8 : 10, textDecoration:'none' }}>
          <img src="/images/gowjee-caterers-logo.png" alt="Gowjee Caterers" style={{ height: isMobile ? 36 : 44, width: 'auto', objectFit: 'contain', display: 'block' }} />
          <div>
            <div style={{ fontFamily:'Cormorant Garamond', fontSize: isMobile ? 14 : 16, fontWeight:600, color:GOLD, letterSpacing:3, lineHeight:1 }}>GOWJEE</div>
            <div style={{ fontFamily:'Montserrat', fontSize: isMobile ? 6 : 7, letterSpacing:4, color:'rgba(196,152,50,0.65)', textTransform:'uppercase' }}>CATERERS</div>
          </div>
        </a>

        {/* Desktop nav — hidden on small screens */}
        <div style={{ display: isMobile ? 'none' : 'flex', alignItems:'center' }}>
          {navLinks.map(l => (
            <div key={l.label} style={{ position:'relative' }}
              onMouseEnter={() => l.sub && setDrop(l.label)}
              onMouseLeave={() => setDrop(null)}>
              <a href={l.href} style={{ display:'flex', alignItems:'center', gap:3, color:'#fff', textDecoration:'none', fontFamily:'Montserrat', fontSize:10, fontWeight:500, letterSpacing:1.5, textTransform:'uppercase', padding:'0 12px', height:76, borderBottom:'2px solid transparent', transition:'all 0.3s' }}
                onMouseOver={e => { (e.currentTarget as HTMLElement).style.color = GOLD; (e.currentTarget as HTMLElement).style.borderBottomColor = GOLD; }}
                onMouseOut={e => { (e.currentTarget as HTMLElement).style.color = '#fff'; (e.currentTarget as HTMLElement).style.borderBottomColor = 'transparent'; }}>
                {l.label}{l.sub && <span style={{ fontSize:7, color:GOLD }}>▼</span>}
              </a>
              {l.sub && drop === l.label && (
                <div style={{ position:'absolute', top:76, left:0, minWidth:170, background:'rgba(10,10,10,0.98)', border:`1px solid rgba(196,152,50,0.2)`, backdropFilter:'blur(8px)', zIndex:999 }}>
                  {l.sub.map(s => (
                    <a key={s} href="#" style={{ display:'block', padding:'11px 18px', color:'rgba(255,255,255,0.72)', textDecoration:'none', fontFamily:'Montserrat', fontSize:10, letterSpacing:1, borderBottom:'1px solid rgba(196,152,50,0.07)', transition:'all 0.2s' }}
                      onMouseOver={e => { (e.currentTarget as HTMLElement).style.color = GOLD; (e.currentTarget as HTMLElement).style.background = 'rgba(196,152,50,0.06)'; }}
                      onMouseOut={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.72)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>{s}</a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Hamburger — visible only on small screens */}
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setMob(true)}
          style={{
            background: 'none', border: `1px solid ${GOLD}`, cursor: 'pointer', padding: 8, display: isMobile ? 'flex' : 'none', flexDirection: 'column', gap: 4,
          }}
        >
          {[0, 1, 2].map(i => <span key={i} style={{ display: 'block', width: 20, height: 1.5, background: GOLD }} />)}
        </button>
      </nav>

      {/* Mobile overlay menu */}
      {mob && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.97)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: 24, overflowY: 'auto',
          }}
        >
          <button type="button" aria-label="Close menu" onClick={() => setMob(false)} style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', color: GOLD, fontSize: 28, cursor: 'pointer', padding: 8 }}>✕</button>
          {navLinks.map(l => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => setMob(false)}
              style={{ color: '#fff', textDecoration: 'none', fontFamily: 'Cormorant Garamond', fontSize: 'clamp(22px, 5vw, 30px)', fontWeight: 300, letterSpacing: 4, transition: 'color 0.3s', padding: '12px 8px' }}
              onMouseOver={e => { (e.currentTarget as HTMLElement).style.color = GOLD; }}
              onMouseOut={e => { (e.currentTarget as HTMLElement).style.color = '#fff'; }}
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
    </>
  );
};

export default Navbar;